import React from 'react';

import cs from 'classnames';
import {Location} from 'history';
import pickBy from 'lodash/pickBy';
import {browserHistory, withRouter} from 'react-router';
import styled from 'styled-components';

import {Filterbar} from 'app/components/general/DashboardPage/Filterbar';
import {PaginatedList} from 'app/general/widgets/PaginatedList';
import StateButton from 'app/general/widgets/StateButton';
import ThreeDotsDropdownMenu from 'app/general/widgets/ThreeDotsDropdownMenu';
import {
  convertThemeLocationParamsToFetchParams,
  useAddTheme,
  useThemes,
} from 'app/kbs/api';
import {SORT_BY_TYPES} from 'app/utils/constants';
import {gt} from 'app/utils/i18n';
import {shallowEqual} from 'app/utils/react-utils';

import {EditThemeMeta} from './EditThemeMeta';
import {ViewThemeMeta} from './ViewThemeMeta';

import type {MenuItemsType} from 'app/general/widgets/ThreeDotsDropdownMenu';
import type {
  KBThemeFetchParamsType,
  KBThemeLocationParamsType,
} from 'app/kbs/types';
import type {InjectedRouter, WithRouterProps} from 'react-router';
import type {
  KBThemeSerializedType,
  KnowledgebaseSerializedType,
  ListResponseType,
} from 'sweet-types';

export const DEFAULT_THEME: KBThemeSerializedType = {
  content_type: 'kbtheme',
  url: '',
  name: 'Sweet Default',
  private: true,
  nav_background: '#005f6b',
  logo_background: '#343838',
  nav_text: '#FFFFFF',
  breadcrumb_text: '#4A5158',
  accent_color: '#008C9E',
  text_color: '#495057',
};

const SORT_OPTIONS = [
  SORT_BY_TYPES.MODIFICATION_DATE,
  SORT_BY_TYPES.CREATION_DATE,
  SORT_BY_TYPES.ALPHABETHICALLY,
  SORT_BY_TYPES.RANK,
];

type RouterProps = WithRouterProps<unknown, KBThemeLocationParamsType>;
type Props = RouterProps & {
  setEditingTheme: (theme: KBThemeSerializedType | null) => unknown;
  editingTheme: KBThemeSerializedType | null;
  kb: KnowledgebaseSerializedType;
  updateTheme: (theme: KBThemeSerializedType) => Promise<any>;
};

export const EditKBTheme = withRouter(
  ({
    editingTheme,
    setEditingTheme,
    kb,
    updateTheme,
    router,
    location,
  }: Props) => {
    const resetFilters = () => {
      router.replace({
        pathname: location.pathname,
      });
      const link = getFilterLink(
        browserHistory,
        browserHistory.getCurrentLocation()
      );
      browserHistory.push(link);
    };
    const viewingAll = !fetchParamsAreDifferent(
      Object.freeze({}),
      location.query
    );

    return (
      <>
        {editingTheme ? (
          <EditThemeMeta
            theme={editingTheme}
            kbName={kb?.name ?? gt('Default')}
            onSubmit={updateTheme}
            onCancel={() => setEditingTheme(null)}
          />
        ) : (
          <>
            <Filterbar
              ct="theme"
              viewingAll={viewingAll}
              show={{
                team: false,
                tags: false,
                sort: true,
                filters: true,
                search: true,
              }}
              resetFilters={resetFilters}
              sortOptions={SORT_OPTIONS}
              hideFilters={[
                'reviewer_unassigned',
                'is_pending_draft',
                'is_draft',
                'needs_approval',
                'mine',
                'signoff',
                'needs_review',
                'disabled_review',
              ]}
            />
            <PaginatedList<KBThemeFetchParamsType, KBThemeSerializedType>
              hook={useThemes}
              params={{
                ...convertThemeLocationParamsToFetchParams(location.query),
                page_size: '10',
              }}
              render={(data) => (
                <ThemeList
                  data={data}
                  kb={kb}
                  updateTheme={updateTheme}
                  editTheme={setEditingTheme}
                />
              )}
            />
            <button
              onClick={(e) => {
                e.preventDefault();
                return setEditingTheme(DEFAULT_THEME);
              }}
              className="btn btn-primary mt-4"
            >
              <i className="fas fa-plus-circle mr-2" />
              {gt('Add New Theme')}
            </button>
          </>
        )}
      </>
    );
  }
);

type ThemeListProps = {
  data?: ListResponseType<KBThemeSerializedType>;
  kb?: KnowledgebaseSerializedType;
  updateTheme: (theme: KBThemeSerializedType) => Promise<any>;
  editTheme: (theme: KBThemeSerializedType) => unknown;
};

const ThemeList = ({data, kb, editTheme, updateTheme}: ThemeListProps) => {
  const [addTheme] = useAddTheme();
  if (data == null || data?.count === 0) {
    return <span>{gt('No themes available.')}</span>;
  }
  return (
    <ResponsiveCardColumns>
      {data.results.map((theme) => {
        const isSelected = kb?.theme?.url === theme.url;
        return (
          <ViewThemeMeta
            key={theme.url}
            theme={theme}
            kbName={theme.name}
            isSelected={isSelected}
          >
            <StateButton
              disabled={isSelected}
              onClick={(e) => {
                e.preventDefault();
                return updateTheme(theme);
              }}
              className={cs('btn', {
                'btn-outline-primary': !isSelected,
                'btn-outline-dark': isSelected,
              })}
              busyText={gt('Selecting...')}
            >
              {isSelected ? gt('Selected') : gt('Select')}
            </StateButton>
            <ThreeDotsDropdownMenu
              actionsGetter={(): MenuItemsType => [
                {
                  key: 'edit',
                  icon: 'far fa-edit',
                  text: gt('Edit'),
                  condition: () => theme.private === true,
                  callback: () => Promise.resolve(editTheme(theme)),
                },
                {
                  key: 'duplicate',
                  icon: 'far fa-copy',
                  text: gt('Duplicate'),
                  condition: () => true,
                  callback: () =>
                    addTheme({...theme, name: `${theme.name} Copy`}).unwrap(),
                },
              ]}
              object={theme}
              forceMenu={true}
            />
          </ViewThemeMeta>
        );
      })}
    </ResponsiveCardColumns>
  );
};

export const getFilterLink = (
  router: InjectedRouter | typeof browserHistory,
  location: Location<any>
) => ({
  pathname: location.pathname,
  query: pickBy({...location.query, page: ''}, (value) => value !== ''),
});

export const fetchParamsAreDifferent = (
  params: KBThemeLocationParamsType,
  nextParams: KBThemeLocationParamsType
) => {
  const fetchParams = convertThemeLocationParamsToFetchParams(params);
  const nextFetchParams = convertThemeLocationParamsToFetchParams(nextParams);
  return !shallowEqual(fetchParams, nextFetchParams);
};

const ResponsiveCardColumns = styled.div.attrs({className: 'card-columns'})`
  @media (max-width: 767.98px) {
    column-count: 1 !important;
  }
  @media (min-width: 768px) and (max-width: 991.98px) {
    column-count: 2 !important;
  }
`;
